import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const TramiteContext = createContext();

export const useTramite = () => {
    const context = useContext(TramiteContext);
    if (!context) throw new Error('useTramite must be used within a TramiteProvider');
    return context;
};

export const TramiteProvider = ({ children }) => {
    const { authFetch, agentInfo, loading: authLoading } = useAuth();
    const [currentTramite, setCurrentTramite] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isResuming, setIsResuming] = useState(true);

    // Debug logging for tramite changes
    useEffect(() => {
        console.log('[TramiteContext] currentTramite updated:', currentTramite);
    }, [currentTramite]);

    const fetchHistory = useCallback(async () => {
        if (!agentInfo?.id) return;

        try {
            const res = await authFetch(`/api/tramite/history/${agentInfo.id}`);
            if (res.ok) {
                const data = await res.json();
                const fetchedHistory = data.history || [];
                setHistory(fetchedHistory);

                // DISBLED AUTO-RESUME: As per user request ("surgical change"), 
                // we no longer auto-resume on refresh. Agents must click a conversation.
                /*
                setCurrentTramite(prev => {
                    if (!prev && fetchedHistory.length > 0) {
                        const latest = fetchedHistory[0];
                        if (latest.status === 'OPEN') {
                            console.log("DEBUG [TramiteProvider] Auto-resuming latest open tramite:", latest.id);
                            return latest;
                        }
                    }
                    return prev;
                });
                */
            }
        } catch (err) {
            console.error("Error fetching tramite history:", err);
        } finally {
            setIsResuming(false);
        }
    }, [agentInfo, authFetch]); // NOTE: No currentTramite dep — use functional update instead

    const startTramite = useCallback(async (contactId = null) => {
        if (!agentInfo?.id) return null;
        setLoading(true);
        try {
            const res = await authFetch('/api/tramite/start', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    agent_id: agentInfo.id,
                    contact_id: contactId
                })
            });
            if (res.ok) {
                const data = await res.json();
                const newTramite = {
                    id: data.tramite_id,
                    status: 'OPEN',
                    contact_id: contactId
                };
                setCurrentTramite(newTramite);
                fetchHistory(); // Refresh history
                return newTramite;
            }
        } catch (err) {
            console.error("Error starting tramite:", err);
        } finally {
            setLoading(false);
        }
        return null;
    }, [agentInfo, authFetch, fetchHistory]);

    const closeTramite = useCallback(async () => {
        if (!currentTramite) return;
        setLoading(true);
        try {
            const res = await authFetch('/api/tramite/close', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tramite_id: currentTramite.id })
            });
            if (res.ok) {
                setCurrentTramite(null);
                fetchHistory();
            }
        } catch (err) {
            console.error("Error closing tramite:", err);
        } finally {
            setLoading(false);
        }
    }, [currentTramite, authFetch, fetchHistory]);

    // Auto-fetch history on agent login - this also handles auto-resume
    useEffect(() => {
        if (authLoading) return; // Wait for authentication to resolve

        if (agentInfo?.id) {
            if (isResuming) {
                fetchHistory();
            }
        } else {
            // Not logged in or no agent info, stop resumption check
            setIsResuming(false);
        }
    }, [agentInfo, authLoading, fetchHistory, isResuming]);

    return (
        <TramiteContext.Provider value={{
            currentTramite,
            setCurrentTramite,
            history,
            loading,
            isResuming,
            startTramite,
            closeTramite,
            fetchHistory
        }}>
            {children}
        </TramiteContext.Provider>
    );
};
